// Keys of the log records a tab or the worker wrote while waiting for their author (BEAST-14120), until the author
// is known and they are settled. Bounded: when the author never comes (a page that never settles its author), a
// key older than maxAgeMs is dropped - the sender has sent that record without an author by then, so settling it
// would change nothing - and at most maxKeys are kept.
// The age counts from when a key is added (after its write), not from the record's own ts: beaver buffers a log for up
// to PERSIST_INTERVAL_MS before writing it, so a key can outlive its record's stale point by about that much. Settling
// such a record then changes nothing, which is harmless.

export const MAX_PENDING_AUTHOR_KEYS = 10_000;

export class PendingAuthorKeys {
    #entries = [];
    #maxAgeMs;
    #maxKeys;
    #now;

    /**
     * @param {Object} options
     * @param {number} options.maxAgeMs - A key older than this is dropped (its record went out without an author)
     * @param {number} [options.maxKeys] - Most keys kept; the oldest go first
     * @param {Function} [options.now] - Clock, for tests
     */
    constructor({ maxAgeMs, maxKeys = MAX_PENDING_AUTHOR_KEYS, now = () => Date.now() }) {
        this.#maxAgeMs = maxAgeMs;
        this.#maxKeys = maxKeys;
        this.#now = now;
    }

    get size() {
        return this.#entries.length;
    }

    add(keys) {
        const at = this.#now();
        keys.forEach((key) => this.#entries.push({ key, at }));
        this.#prune(at);
    }

    // Takes every key that can still be settled and empties the list.
    take() {
        this.#prune(this.#now());
        const keys = this.#entries.map((entry) => entry.key);
        this.#entries = [];
        return keys;
    }

    // Entries are added in time order, so the stale ones are always at the front.
    #prune(at) {
        let stale = 0;
        while (stale < this.#entries.length && at - this.#entries[stale].at >= this.#maxAgeMs) stale += 1;
        const overflow = Math.max(0, this.#entries.length - stale - this.#maxKeys);
        if (stale + overflow) this.#entries.splice(0, stale + overflow);
    }
}
